import React, { Component, useState, useEffect, useContext } from 'react';
import { useImmer } from 'use-immer';
import { useParams, useLocation, useHistory, Prompt } from "react-router-dom";
import { useFormik } from 'formik';
import * as Yup from "yup";
import _ from 'lodash';
import parse from "html-react-parser"
import { useDispatch } from 'react-redux'
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { modalActionCreator } from '../../store/actions/modalActions';
import { LinkContainer } from 'react-router-bootstrap';
import { applicationContextActionCreator } from '../../store/actions';
import { Card, Button, Form, Col, Tabs, Tab, ButtonToolbar, Dropdown, ToggleButton, ButtonGroup } from 'react-bootstrap';
import { CustomerAccountService } from '../../services';
import { TabList, TabItem, CodingEventTable } from '../../controls';
import { CustomerUsers } from '../';
import { customerInitData, customerValidationSchema, getContactInfo } from './CustomerAccount/CustomerAccount.Code';
import CustomerAccountForm from './CustomerAccount/CustomerAccount.Form';
import FormControls, { ConditionalRender } from '../../controls/formControls';
import { AuthContext } from '../../contexts/AuthContext';
import { CustomerAccountDetails, ParentCustomerAccountDetails } from './CustomerAccountDetails';
import { userTypes } from '../../utilities/appConstants';

const Customer = (props) => {
    const [canEdit, setCanEdit] = useImmer(false);
    const [customer, setCustomer] = useImmer(customerInitData);
    const [vendors, setVendors] = useState([]);
    const [queues, setQueues] = useState([]);
    const [transitioned, setTransitioned] = useState(false);

    const authContext = useContext(AuthContext);
   
    let { id } = useParams();

    const dispatch = useDispatch();

    useEffect(() => {
        const values = [authContext.claimValues.createReadUpdateDelete, authContext.claimValues.readUpdate];

        const canEdit = authContext.hasPermission(authContext.claims.canCrudAccounts, values)
        setCanEdit(canEdit);
        getCustomer();
    }, [id])

    const getCustomer = async () => {

        if (id !== undefined && id > 0) {
            var customerService = new CustomerAccountService();
            var results = await customerService.getCustomer(id);
            results.model.account.users = results.model.account.users
                .sort((a, b) => (a.lastName.toLowerCase() > b.lastName.toLowerCase()) ? 1 : -1);
            setCustomer(results.model.account);
            setVendors(results.model.vendors);
            setQueues(results.model.queues);
            const customerTrans = isTransitioned(results.model.account.users);
            setTransitioned(customerTrans);
        }
    }

    const isTransitioned = (users) => {
        var users = users?.filter(x => { return x.userType === userTypes.customerUser });
        var isTransitioned = users?.length > 0 ? true : false;
        return isTransitioned;
    }

    const getContacts = () => {
        var contacts = customer.users?.filter(x => { return x.userType === userTypes.contact });
        contacts = contacts ? contacts : [];
        return contacts;
    }

    const removeChildAccount = (id) => {
        setCustomer(draft => {
            draft.childAccounts = draft.childAccounts.filter(x => { return x.id != id });
        })
    }

    const addChildAccount = (account) => {
        setCustomer(draft => {
            draft.childAccounts.push(account);
            draft.childAccounts = draft.childAccounts.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : -1)
        })
    }

    const saveCustomer = async (values) => {
        var customerService = new CustomerAccountService();
        var results = await customerService.updateCustomer(values);
        if (results.result) {
            //var customer = results.model;
            setCustomer(results.model);
        }
        else {
            showModal({
                open: true,
                message: parse(`There was an error saving this account.<br/><br/> Account Name: ${customer.name}<br/>Account Id: ${customer.id}<br/><br/>Please try again.`),
                title: `Failed to save account: ${customer.name}!`,
                closeModal: closeModal
            }, 'errorModal')
        }
    }

    const formik = useFormik({
        initialValues: customer,
        validationSchema: Yup.object().shape(customerValidationSchema),
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
            await saveCustomer(values);
            setSubmitting(false);
        }
    });

    const submitForm = () => {
        if (!_.isEmpty(formik.errors)) {
            const errors = Object.keys(formik.errors).map(errorKey => {
                return formik.errors[errorKey];
            })
            const errorString = parse(_.join(errors, '<br/>'));
            showModal({
                open: true,
                message: errorString,
                title: 'Validation Errors!',
                closeModal: closeModal
            }, 'errorModal')
        } else {
            formik.submitForm();
        }
    }

    const notifyUserChange = (user) => {
        const newUser = {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            fullName: user.fullName,
            userType: user.userType,
            roleName: user.roleName,
            userTypeFriendly: user.userTypeFriendly,
            emailAddress: user.emailAddress,
            phoneNumber: user.phoneNumber?.number,
            state: user.state
        };

        setCustomer(draft => {
            if (draft.users === undefined) {
                draft.users = [];
            }
            _.remove(draft.users, item => item.id === newUser.id);
            if (newUser.state !== -100) {
                draft.users.push(newUser);
            }
            draft.users = draft.users.sort((a, b) => (a.lastName.toLowerCase() > b.lastName.toLowerCase()) ? 1 : -1)
        })
    }

    const updateClinician = (clinician) => {
        setCustomer(draft => {
            _.remove(draft.clinicians, item => item.id === clinician.id);
            if (clinician.state !== -100) {
                draft.clinicians.push(clinician)
            }
            draft.clinicians = _.sortBy(draft.clinicians, 'lastName');
        });
    }

    const showModal = (props, type) => {
        dispatch(modalActionCreator.showModal(props, type));
    }

    const closeModal = () => {
        dispatch(modalActionCreator.hideModal());
    }

    const transitionAccountSuccess = () => {
        getCustomer();
        closeModal();
    }

    return (
        <>
            <Prompt
                when={formik.dirty}
                message="You have unsaved changes, are you sure you want to leave?"
            />
            <Card>
                <Card.Header className="d-flex align-items-center">
                    <div className="mr-auto" >{customer.name + " - " + customer.id}</div>
                    <div className="card-btn-group">
                        <ConditionalRender visible={canEdit && !formik.dirty && !transitioned && !customer.isParentAccount}>
                            <Button variant="success" size="sm" onClick={() => showModal({
                                open: true,
                                model: { customerId: customer.id, contacts: getContacts()},
                                title: `Transition Account: ${customer.name} - ${customer.id}`,
                                onSuccess: transitionAccountSuccess,
                                closeModal: closeModal,
                                size: 'xl'
                            }, 'transitionCustomerAccount')}>Transition Account</Button>
                        </ConditionalRender>
                        <LinkContainer to={'/admin/accounts'}>
                            <Button variant="secondary" size="sm">Close</Button>
                        </LinkContainer>
                        <ConditionalRender visible={canEdit && formik.dirty}>
                            <Button variant="success" size="sm" onClick={() => submitForm()}>Save</Button>
                        </ConditionalRender>
                    </div>
                </Card.Header>
                <Card.Body>
                    <CustomerAccountForm formHandler={formik} vendors={vendors} queues={queues} customer={customer} />
                        {
                            customer.isParentAccount ?
                                <ParentCustomerAccountDetails
                                    customer={customer}
                                    showModal={showModal}
                                    closeModal={closeModal}
                                    addChildAccount={addChildAccount}
                                    removeChildAccount={removeChildAccount}
                                    notifyUserChange={notifyUserChange}
                                /> :
                                <CustomerAccountDetails
                                    formik={formik}
                                    customer={customer}
                                    showModal={showModal}
                                    updateClinician={updateClinician}
                                    closeModal={closeModal}
                                    notifyUserChange={notifyUserChange}
                                    getContactInfo={getContactInfo} />
                        }
                </Card.Body>
            </Card>
        </>
    );
}

export default Customer;